"use client"

// CSS imports
import "@glidejs/glide/dist/css/glide.core.min.css"

// React and Next.js imports
import React, { useEffect, useMemo, useRef } from "react"
import { useParams } from "next/navigation"

// Third-party library imports
import { Message } from "@locmod/intl"
import { useGames } from "@azuro-org/sdk"
import { Game_OrderBy } from "@azuro-org/toolkit"
import cx from "classnames"
import Glide from "@glidejs/glide"

// Local component imports
import { Icon } from "components/ui"
import EmptyContent from "../EmptyContent/EmptyContent"

// Local utility and hook imports
import { useLeagueSortedMarkets } from "@/hooks/useLeagueSortedMarkets"
import useChainProviderHook from "@/hooks/useChainProvider"
import { type SupportedNetwork } from "@/Overtime/types/network"
import useSXPopularMarkets from "@/SX/hooks/useSXPopularMarkets"

// Local file imports
import messages from "./messages"
import OvertimeTopEventCard from "./OvertimeTopEventCard"
import AzuroTopEventCard from "./AzuroTopEventCard"
import SXTopEventCard from "./SXTopEventCard"

const CardSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={cx("bone h-[12.125rem] w-full rounded-md", className)} />
  )
}

const sliderConfiguration = {
  gap: 8,
  perView: 3,
  startAt: 0,
  focusAt: 0,
  autoplay: 5000,
  bound: true,
  breakpoints: {
    802: {
      perView: 1.1,
    },
  },
}

const Events: React.FC = () => {
  const { isOvertimeChain, isAzuroChain, isSXChain, chainId } =
    useChainProviderHook()
  const { data: azuroGames, isFetching: isAzuroFetching } = useGames({
    filter: {
      limit: 9,
    },
    orderBy: Game_OrderBy.Turnover,
    query: {
      enabled: isAzuroChain,
    },
  })

  const { sortedMarkets: overtimeGames, isLoading: isMarketsLoading } =
    useLeagueSortedMarkets({
      networkId: chainId as SupportedNetwork,
      maxGames: 9,
    })

  const { markets: sxGames, isLoading: isSXLoading } =
    useSXPopularMarkets(isSXChain)

  const games = (() => {
    if (isOvertimeChain) {
      return overtimeGames
    }
    if (isSXChain) {
      return sxGames
    }
    return azuroGames
  })()

  const isFetching = (() => {
    if (isOvertimeChain) {
      return isMarketsLoading
    }
    if (isSXChain) {
      return isSXLoading
    }
    return isAzuroFetching
  })()

  const containerRef = useRef<HTMLDivElement>(null)

  // Memoize the card component selection to avoid re-evaluation on every render
  const getCardComponent = useMemo(() => {
    if (isOvertimeChain) {
      return OvertimeTopEventCard
    }
    if (isSXChain) {
      return SXTopEventCard
    }
    return AzuroTopEventCard
  }, [isOvertimeChain, isSXChain])

  useEffect(() => {
    if (!games?.length || isFetching) {
      return
    }

    const slider = new Glide(containerRef.current, sliderConfiguration)

    slider.mount()

    return () => {
      slider?.destroy()
    }
  }, [games, isFetching])

  if (isFetching) {
    return (
      <div className="flex items-center justify-between mt-6 space-x-2">
        <CardSkeleton />
        <CardSkeleton className="mb:hidden" />
        <CardSkeleton className="mb:hidden" />
      </div>
    )
  }

  // Show empty content if no games found
  if (!games || games.length === 0) {
    return <EmptyContent />
  }

  return (
    <div ref={containerRef} className="glide !static group mt-6 mb-6">
      <div className="glide__track" data-glide-el="track">
        <ul className="glide__slides">
          {games?.map((game, index) => {
            const CardComponent = getCardComponent
            return (
              <li key={index} className="glide__slide overflow-hidden">
                <CardComponent game={game} />
              </li>
            )
          })}
        </ul>
      </div>
      <div
        className="absolute top-6 right-6 flex items-center"
        data-glide-el="controls"
      >
        <button
          className="w-8 h-6 flex items-center justify-center bg-accent-grey-980 rounded-tl-full rounded-tr-1 rounded-br-1 rounded-bl-full border border-accent-grey-800 text-accent-grey-200 hover:text-accent-grey-20 transition backdrop-blur-[17px] "
          data-glide-dir="<"
        >
          <Icon className="size-5" name="interface/chevron_left" />
        </button>
        <button
          className="w-8 h-6 flex items-center justify-center bg-accent-grey-980 rounded-tl-1 rounded-tr-full rounded-br-full rounded-bl-1 border border-accent-grey-800 text-accent-grey-200 hover:text-accent-grey-20 transition ml-1 backdrop-blur-[17px] "
          data-glide-dir=">"
        >
          <Icon className="size-5" name="interface/chevron_right" />
        </button>
      </div>
    </div>
  )
}

const TopEvents: React.FC = () => {
  const params = useParams()
  const sport = messages[params!.sportSlug as string]

  return (
    <div className="relative pt-6">
      <div className="px-4">
        <Message
          className="text-caption-13 text-accent-grey-200 uppercase"
          value={messages.title}
        />
        <h1 className="text-heading-h2 ds:text-heading-h1 font-bold">
          <Message className="text-brand-light-500" value={messages.top} />
          {Boolean(sport) && <Message className="ml-2" value={sport} />}
          <Message className="ml-2" value={messages.events} />
        </h1>
      </div>
      <Events />
    </div>
  )
}

export default TopEvents
